import i18n from '@/utils/i18n';
import {
  OrderEntrySummaryData,
  useSalesPortalOrderSummaryLoader,
} from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/loaders/use-sales-portal-order-summary-loader';
import {
  OrderSummaryMetricRow,
  useOrderSummaryMetricRows,
} from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/hooks/use-order-summary-metric-rows';
import {
  OrderValidation,
  useSaveAndValidateSalesPortalCartMutator,
} from '@/experiences/sales-portal/domain/ordering/blocs/mutators/use-save-sales-portal-cart-mutator';
import {
  selectSalesPortalCartOrderId,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useConfidentialVisibilityStore } from '@/experiences/sales-portal/domain/ordering/blocs/stores/order-entry-confidential-visibility-store';
import { useShallow } from 'zustand/react/shallow';

/**
 * Appends the estimated-commission row to a single order's summary rows.
 *
 * @param rowsPerSplit - Summary rows, one array per fulfillment split
 * @param commissionDisplayValue - Formatted commission, or null when unavailable
 * @param showConfidentialFields - Confidential-field visibility toggle
 * @returns Rows per split, with a commission row appended where applicable
 */
export const appendCommissionRow = (
  rowsPerSplit: OrderSummaryMetricRow[][],
  commissionDisplayValue: string | null | undefined,
  showConfidentialFields: boolean,
): OrderSummaryMetricRow[][] => {
  if (
    !commissionDisplayValue ||
    !showConfidentialFields ||
    rowsPerSplit.length > 1
  ) {
    return rowsPerSplit;
  }
  return rowsPerSplit.map((rows) => [
    ...rows,
    {
      key: 'commission',
      label: i18n.t(
        'sales_portal.order_entry.order_summary_bottom_bar.metric.commission.title',
      ),
      value: commissionDisplayValue,
    },
  ]);
};

interface OrderEntryCheckoutSummaryArgs {
  commissionDisplayValue?: string | null;
  isGrossProfitHidden: boolean;
  open: boolean;
  refetchCommission?: (() => void) | null;
  restaurantUUID: string;
  supplierUUID: string;
}

interface OrderEntryCheckoutSummary {
  canPlaceOrder: boolean;
  firstSummary: OrderEntrySummaryData | undefined;
  hasError: boolean;
  isLoading: boolean;
  isSummaryEmpty: boolean;
  orderUUID: string | null;
  orderValidation: OrderValidation | null;
  splitOrderCount: number;
  splitOrderMetricRows: OrderSummaryMetricRow[][];
  summarizeOrder: () => Promise<void>;
}

/**
 * Validates the cart and loads the order summary for the checkout sheet
 *
 * @param commissionDisplayValue - Formatted estimated commission; adds a
 *   commission row to the summary metrics when set
 * @param isGrossProfitHidden - Hides GP rows in the summary metrics
 * @param open - Runs the validate-and-summarize flow when it turns true
 * @param refetchCommission - Called after a successful save so commission
 *   reflects the saved order
 * @param restaurantUUID - Restaurant for the order summary query
 * @param supplierUUID - Supplier for the order summary query
 * @returns Validation state, summary data, and a retry via `summarizeOrder`
 */
export const useOrderEntryCheckoutSummary = ({
  commissionDisplayValue,
  isGrossProfitHidden,
  open,
  refetchCommission,
  restaurantUUID,
  supplierUUID,
}: OrderEntryCheckoutSummaryArgs): OrderEntryCheckoutSummary => {
  const orderUUID = useCartStore(useShallow(selectSalesPortalCartOrderId));
  const { isSavingAndValidatingCart, orderValidation, saveAndValidateCart } =
    useSaveAndValidateSalesPortalCartMutator();
  const { data, error, loading, loadOrderSummary, reset } =
    useSalesPortalOrderSummaryLoader({ isGrossProfitHidden });
  const [summarizeFailed, setSummarizeFailed] = useState(false);

  const summarizeOrder = useCallback(async () => {
    setSummarizeFailed(false);
    reset();

    const success = await saveAndValidateCart();
    const savedOrderUUID = useCartStore.getState().getCart()?.orderUUID ?? null;
    if (success && savedOrderUUID) {
      loadOrderSummary({
        variables: {
          orderIds: [savedOrderUUID],
          restaurantUUID,
          supplierUUID,
        },
      });
      refetchCommission?.();
    } else {
      setSummarizeFailed(true);
    }
  }, [
    loadOrderSummary,
    refetchCommission,
    reset,
    restaurantUUID,
    saveAndValidateCart,
    supplierUUID,
  ]);

  useEffect(() => {
    if (open) {
      summarizeOrder();
    }
    // Refetch only when the sheet opens, not when summarizeOrder's deps change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hasValidatedOrder =
    !!orderUUID && !isSavingAndValidatingCart && !!orderValidation;
  const canPlaceOrder = orderValidation?.canPlaceOrder ?? false;

  const firstSummary = data?.[0];
  const baseMetricRows = useOrderSummaryMetricRows(
    firstSummary?.splitOrderMetrics ?? [],
    firstSummary?.currency ?? '',
    { isGrossProfitHidden },
  );
  const showConfidentialFields = useConfidentialVisibilityStore(
    (state) => state.showConfidentialFields,
  );

  const splitOrderMetricRows = useMemo(
    () =>
      appendCommissionRow(
        baseMetricRows,
        commissionDisplayValue,
        showConfidentialFields,
      ),
    [baseMetricRows, commissionDisplayValue, showConfidentialFields],
  );
  const splitOrderCount = firstSummary?.splitOrderMetrics?.length ?? 0;

  const summaryLoading =
    !orderUUID || loading || (!data && !summarizeFailed && !error);
  const isLoading = !hasValidatedOrder || summaryLoading;
  const hasError = (!!error && !isLoading) || summarizeFailed;
  const isSummaryEmpty = !isLoading && !hasError && splitOrderCount === 0;

  return {
    canPlaceOrder,
    firstSummary,
    hasError,
    isLoading,
    isSummaryEmpty,
    orderUUID,
    orderValidation: orderValidation ?? null,
    splitOrderCount,
    splitOrderMetricRows,
    summarizeOrder,
  };
};
